"use client";

import type { PlainDate, PlainDateString } from "@sa-nu/shared-utils/date";
import { type ReactNode, useState } from "react";

import { DxIcon } from "../../../dx/ui/icons";
import { ARCHITECTURE_FILTER_OPTIONS } from "../../../reservation-filters/architecture-filter-options";
import type { BrowseArea } from "../../_hooks/use-room-type-search";
import type { SearchCalendarDateInfo } from "../../_utils/build-search-calendar-date-info-map";
import { FilterChip } from "../../ui/filter-chip";
import { ChevronDownIcon } from "../../ui/i/chevron-down";

import { useTriggerToggle } from "./_hooks/use-trigger-toggle";
import { formatDateRangeChipLabel } from "./_utils/format-date-range-chip-label";
import { AreaFilterPopup } from "./area-filter-popup";
import type { ApplyConditions } from "./condition-filter-popup";
import { ConditionFilterPopup } from "./condition-filter-popup";
import { DateFilterPopup } from "./date-filter-popup";
import { FACILITY_FILTERS } from "./facility-filter-options";

type ActivePopup = "date" | "area" | "condition";

// 設備名テーブル（チップのラベル表示用）。slug → label。設備リストの単一の真実から導出。
const FACILITY_LABEL: Record<string, string> = Object.fromEntries(
  FACILITY_FILTERS.map((f) => [f.slug, f.label]),
);

/**
 * SP（<1024px）用の検索バー。
 *
 * Figma（5171-17491 適用状態 / 5171-17138 ポップアップ状態）準拠:
 * - 検索バー h-[54px]、下ボーダー
 * - 日程フィールド（固定ラベル "日程"）flex-1
 * - エリアフィールド（固定ラベル "エリア"）flex-1
 * - 絞り込みアイコン（tune 系）w-[60px]
 * - 各フィールドクリックで SP 用 Popover（bottom-start・全幅）を開く
 * - バー下の横スクロールチップ行（適用中フィルターのみ表示）
 *
 * PC（≥1024px）では browse-layout.tsx が lg:hidden で非表示にする。
 */
export function SpTopbar({
  areas,
  selectedAreaSlugs,
  onToggleArea,
  onApplyAreaSlugs,
  selectedFacilities,
  onToggleFacility,
  evChargerAvailable,
  onToggleEvCharger,
  selectedArchTypes,
  onToggleArchType,
  largeOnly,
  onToggleLargeOnly,
  onApplyConditions,
  firstNight,
  lastNight,
  onSetStayRange,
  onClearStayRange,
  dateInfoMap,
  dateInfoMapLoading,
}: {
  areas: BrowseArea[];
  selectedAreaSlugs: ReadonlySet<string>;
  onToggleArea: (slug: string) => void;
  onApplyAreaSlugs: (slugs: string[]) => void;
  selectedFacilities: ReadonlySet<string>;
  onToggleFacility: (slug: string) => void;
  evChargerAvailable: boolean;
  onToggleEvCharger: () => void;
  selectedArchTypes: ReadonlySet<string>;
  onToggleArchType: (key: string) => void;
  largeOnly: boolean;
  onToggleLargeOnly: () => void;
  onApplyConditions: (conditions: ApplyConditions) => void;
  firstNight: PlainDate | null;
  lastNight: PlainDate | null;
  onSetStayRange: (firstNight: PlainDate, lastNight: PlainDate) => void;
  onClearStayRange: () => void;
  /** 日付ごとの最低価格・在庫状況。DateFilterPopup へそのまま中継する */
  dateInfoMap?: Record<PlainDateString, SearchCalendarDateInfo>;
  /** dateInfoMap 取得中かどうか。DateFilterPopup へそのまま中継する */
  dateInfoMapLoading?: boolean;
}) {
  const [activePopup, setActivePopup] = useState<ActivePopup | null>(null);
  // 各フィールドのトグル。PC トリガーと同じ useTriggerToggle を使い、outside-press の close と
  // onClick の再オープン競合を避ける（pointerdown 時点の active を退避して再オープンを抑止）。
  const dateToggle = useTriggerToggle(
    activePopup === "date",
    () => setActivePopup("date"),
    () => setActivePopup(null),
  );
  const areaToggle = useTriggerToggle(
    activePopup === "area",
    () => setActivePopup("area"),
    () => setActivePopup(null),
  );
  const conditionToggle = useTriggerToggle(
    activePopup === "condition",
    () => setActivePopup("condition"),
    () => setActivePopup(null),
  );

  // 検索バー全体を各ポップアップのアンカーとして使う（callback ref で state に保持）
  const [barEl, setBarEl] = useState<HTMLDivElement | null>(null);

  const hasDateValue = firstNight !== null && lastNight !== null;
  const hasAreaValue = selectedAreaSlugs.size > 0;
  const hasConditionValue =
    selectedArchTypes.size > 0 ||
    selectedFacilities.size > 0 ||
    largeOnly ||
    evChargerAvailable;
  const hasAnyFilter = hasDateValue || hasAreaValue || hasConditionValue;

  // 日程チップのラベル
  const dateChipLabel = formatDateRangeChipLabel(firstNight, lastNight);

  const clearAll = () => {
    onClearStayRange();
    onApplyAreaSlugs([]);
    onApplyConditions({
      architectureTypes: [],
      facilities: [],
      largeOnly: false,
      evCharger: false,
    });
  };

  return (
    <div className="bg-dx-surface-base">
      <div
        ref={(el) => setBarEl(el)}
        className="border-dx-border-default flex h-[54px] items-center gap-[4px] border-b px-[16px]"
      >
        <SpSearchField
          icon={
            <DxIcon src="/i/dx/calendar.svg" className="text-dx-text-default" />
          }
          label="日程"
          hasValue={hasDateValue}
          onPointerDown={dateToggle.onPointerDown}
          onClick={dateToggle.onClick}
          data-analytics-id="room_type_browse_opened_date_filter"
        />

        <SpSearchField
          icon={<DxIcon src="/i/dx/map.svg" className="text-dx-text-default" />}
          label="エリア"
          hasValue={hasAreaValue}
          onPointerDown={areaToggle.onPointerDown}
          onClick={areaToggle.onClick}
          data-analytics-id="room_type_browse_opened_area_filter"
        />

        <SpSearchField
          icon={
            <DxIcon
              src="/i/dx/tune.svg"
              className={
                hasConditionValue
                  ? "text-dx-text-default"
                  : "text-dx-text-muted"
              }
            />
          }
          hasValue={hasConditionValue}
          onPointerDown={conditionToggle.onPointerDown}
          onClick={conditionToggle.onClick}
          ariaLabel="絞り込み条件"
          fixedWidth
          data-analytics-id="room_type_browse_opened_condition_filter"
        />
      </div>

      {/* 適用中フィルターのチップ横スクロール行（Figma 5171-17491 条件検索行）
          フィルターが1件以上適用されている場合のみ表示する。
          横スクロール＋「クリア」リンク。 */}
      {hasAnyFilter && (
        <div className="border-dx-border-default flex items-center gap-[8px] overflow-x-auto border-b px-[16px] py-[8px]">
          {dateChipLabel !== null && (
            <FilterChip label={dateChipLabel} onRemove={onClearStayRange} />
          )}

          {areas
            .filter((a) => selectedAreaSlugs.has(a.slug))
            .map((area) => (
              <FilterChip
                key={area.id}
                label={area.name}
                onRemove={() => onToggleArea(area.slug)}
              />
            ))}

          {[...selectedArchTypes].map((value) => (
            <FilterChip
              key={value}
              // ARCHITECTURE_FILTER_OPTIONS に無い拠点固有の値（例: arch=CURRENT）も
              // 選択済みならチップとして表示できるよう、実データ起点＋ラベルfallbackにする
              label={
                ARCHITECTURE_FILTER_OPTIONS.find((o) => o.value === value)
                  ?.label ?? value
              }
              onRemove={() => onToggleArchType(value)}
            />
          ))}

          {Array.from(selectedFacilities).map((slug) => (
            <FilterChip
              key={slug}
              label={FACILITY_LABEL[slug] ?? slug}
              onRemove={() => onToggleFacility(slug)}
            />
          ))}

          {largeOnly && (
            <FilterChip label="Largeのみ" onRemove={onToggleLargeOnly} />
          )}

          {evChargerAvailable && (
            <FilterChip label="EV充電器" onRemove={onToggleEvCharger} />
          )}

          <button
            data-analytics-id="room_type_browse_clicked_applied_filters_clear"
            type="button"
            onClick={clearAll}
            className="font-notojp text-dx-text-default focus-visible:ring-dx-border-focus shrink-0 text-[12px] leading-none underline focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:outline-none"
          >
            クリア
          </button>
        </div>
      )}

      {/* display:none の領域（フォーカス/AT からも外れる）。各 Popup の DOM はここに置くが、
          ポップアップ本体は Popover.Portal 経由で body 直下に描画される。
          externalOpen=true でトリガー、アンカーは検索バー全体（barRef.current）。 */}
      <div className="hidden">
        <DateFilterPopup
          firstNight={firstNight}
          lastNight={lastNight}
          onConfirm={onSetStayRange}
          onClear={onClearStayRange}
          externalOpen={activePopup === "date"}
          onExternalClose={() =>
            setActivePopup((prev) => (prev === "date" ? null : prev))
          }
          variant="filter-search"
          externalAnchorEl={barEl}
          dateInfoMap={dateInfoMap}
          dateInfoMapLoading={dateInfoMapLoading}
        />
        <AreaFilterPopup
          areas={areas}
          selectedAreaSlugs={selectedAreaSlugs}
          onToggleArea={onToggleArea}
          onApplyAreaSlugs={onApplyAreaSlugs}
          externalOpen={activePopup === "area"}
          onExternalClose={() =>
            setActivePopup((prev) => (prev === "area" ? null : prev))
          }
          variant="filter-search"
          externalAnchorEl={barEl}
        />
        <ConditionFilterPopup
          selectedArchTypes={selectedArchTypes}
          onToggleArchType={onToggleArchType}
          selectedFacilities={selectedFacilities}
          onToggleFacility={onToggleFacility}
          largeOnly={largeOnly}
          onToggleLargeOnly={onToggleLargeOnly}
          evChargerAvailable={evChargerAvailable}
          onToggleEvCharger={onToggleEvCharger}
          onApplyConditions={onApplyConditions}
          externalOpen={activePopup === "condition"}
          onExternalClose={() =>
            setActivePopup((prev) => (prev === "condition" ? null : prev))
          }
          variant="filter-search"
          externalAnchorEl={barEl}
        />
      </div>
    </div>
  );
}

// 検索バー内の個別フィールド（下線 input 風ボタン）
function SpSearchField({
  icon,
  label,
  hasValue,
  onClick,
  onPointerDown,
  fixedWidth,
  ariaLabel,
  ...analytics
}: {
  icon: ReactNode;
  label?: string;
  hasValue: boolean;
  onClick: () => void;
  onPointerDown?: () => void;
  fixedWidth?: boolean;
  ariaLabel?: string;
  /** 開くポップアップがフィールドごとに違うため、id は呼び出し側で決める */
  "data-analytics-id": string;
}) {
  return (
    <button
      {...analytics}
      type="button"
      onClick={onClick}
      onPointerDown={onPointerDown}
      aria-label={label === undefined ? ariaLabel : undefined}
      className={`border-dx-border-default focus-visible:ring-dx-border-focus hover:bg-dx-bg-base-hover flex h-[38px] items-center gap-[4px] rounded-t-[2px] border-b px-[8px] py-[6px] transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:outline-none ${fixedWidth ? "w-[60px] shrink-0" : "flex-1"}`}
    >
      <span className="flex size-[20px] shrink-0 items-center justify-center">
        {icon}
      </span>
      <span
        className={`font-notojp min-w-px flex-1 truncate text-left text-[14px] leading-[24px] ${hasValue ? "text-dx-text-default" : "text-dx-text-muted"}`}
      >
        {label}
      </span>
      <ChevronDownIcon className="text-dx-text-default size-[24px] shrink-0" />
    </button>
  );
}
